<template>
    <v-card flat color="transparent" class="fill-height w-100">
        <v-card-title class="d-block">
            <v-btn class="mb-3" small text color="secondary" @click="$router.go(-1)">
                <v-icon left>mdi-arrow-left</v-icon>
                Back
            </v-btn>
            <div>Tasks</div>
        </v-card-title>
        <v-card-subtitle>{{ totalTasks }} tasks</v-card-subtitle>
        <v-card-text>
            <v-skeleton-loader v-if="showInitialLoadingState" class="mx-auto" type="table"></v-skeleton-loader>
            <v-alert v-else-if="showErrorState" type="error" text prominent>
                <div class="d-flex flex-column" style="max-width: 700px">
                    <span class="font-weight-bold">Could not load tasks</span>
                    <span>Please try again. If the problem persists, check your connection or permissions.</span>
                </div>
                <template v-slot:append>
                    <v-btn color="error" outlined small @click="fetchTasks">Retry</v-btn>
                </template>
            </v-alert>
            <template v-else>
                <div class="d-flex align-center">
                    <v-text-field
                        v-model="search"
                        placeholder="Search for a task..."
                        dense
                        hide-details
                        prepend-inner-icon="mdi-magnify "
                        solo
                        flat
                        clearable
                        class="mr-2"></v-text-field>
                    <v-btn @click="fetchTasks" icon>
                        <v-icon>refresh</v-icon>
                    </v-btn>
                </div>
                <div class="d-flex align-center flex-wrap mt-4">
                    <v-sheet rounded class="py-1 px-2 mb-2 mr-2" v-for="(taskType, key) in taskCheckBoxData" :key="key">
                        <v-checkbox class="ma-0 pa-0" v-model="taskType.value" @change="changeSelectedTasks" dense hide-details>
                            <template v-slot:label>
                                <span class="secondary--text caption text-uppercase">{{ taskType.label }}</span>
                            </template>
                        </v-checkbox>
                    </v-sheet>
                </div>

                <v-data-table
                    :server-items-length="totalTasks"
                    :loading="fetchingUserTasks"
                    :headers="headers"
                    :items="userTasks"
                    :options.sync="options"
                    item-key="tkid"
                    :footer-props="{
                        itemsPerPageOptions
                    }"
                    class="mt-4"
                    no-data-text="No user tasks found">
                    <template v-slot:[`item.status`]="{ item }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-icon v-on="on" :color="getTaskChipColor(item.status)" left>
                                    {{ getTaskChipIcon(item.status) }}
                                </v-icon>
                                <v-btn @click="cancelThisTask(item)" v-if="taskIsCancellable(item.status)" fab small elevation="0">
                                    <v-icon color="red darken-2">close</v-icon>
                                </v-btn>
                            </template>
                            <span class="white--text">{{ taskTypeLabel(item.status) }}</span>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`item.operation`]="{ item }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <span class="text-truncate" v-on="on">{{ truncateText(item.operation, 60) }}</span>
                            </template>
                            <span>{{ item.operation }}</span>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`item.started`]="{ item }">
                        <div class="d-flex flex-column py-3">
                            <span v-if="item.started">
                                {{ item.started | dateTimeToHuman }}
                            </span>
                            <span v-else>-</span>
                            <span class="caption" v-if="item.finished">
                                <span class="font-weight-bold">Duration:</span>
                                {{ calculateDuration(item.finished, item.started) }}
                            </span>
                        </div>
                    </template>
                    <template v-slot:[`item.result`]="{ item }">
                        <UserTaskOutcome :message="getItemMessage(item)"></UserTaskOutcome>
                    </template>
                </v-data-table>
            </template>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { calculateDuration, truncateText } from '@/utils'
import { filter, debounce, find, isEqual } from 'lodash'

const UserTaskOutcome = () => import('../components/TheUserTaskOutcome')

export default {
    name: 'UserTasks',
    mixins: [enumsData],
    components: { UserTaskOutcome },
    data() {
        return {
            search: '',
            tasksLoadError: false,
            options: {},
            params: {},
            searchInTasks: null,
            headers: [
                { text: 'Status', align: 'center', value: 'status', width: '80px' },
                {
                    text: 'Task',
                    value: 'operation',
                    width: '300px'
                },
                { text: 'Start time', value: 'started', width: '200px' },
                { text: 'Outcome', value: 'result', width: '50px', align: 'end', sortable: false }
            ],
            taskCheckBoxData: [
                {
                    key: 'RUNNING',
                    label: 'RUNNING',
                    value: true
                },
                {
                    key: 'QUEUED',
                    label: 'QUEUED',
                    value: true
                },
                {
                    key: 'COMPLETED',
                    label: 'COMPLETED',
                    value: true
                },
                {
                    key: 'CANCELLED',
                    label: 'CANCELLED',
                    value: true
                },
                {
                    key: 'FAILED',
                    label: 'FAILED',
                    value: true
                },
                {
                    key: 'CONTAINS_ERRORS',
                    label: 'CONTAINS ERRORS',
                    value: true
                },
                {
                    key: 'CREATED',
                    label: 'CREATED',
                    value: true
                },
                {
                    key: 'REJECTED',
                    label: 'REJECTED',
                    value: true
                }
            ],
            colors: ['warning', 'purple', 'brown', 'info', 'success', 'error', 'info', 'orange', 'red'],
            icons: ['error_outline', 'alarm_on', 'pause', 'mdi-timer-sand', 'done', 'error', '', 'error_outline', 'close']
        }
    },
    created() {
        this.changeSelectedTasks()
        this.$store.dispatch('userStore/fetchRunningUserTasks')
        this.searchInTasks = debounce(() => {
            this.options.page = 1
            this.refetch()
        }, 700)
    },
    computed: {
        ...mapState('userStore', ['userTasks', 'fetchingUserTasks', 'totalTasks', 'maxTaskPerPage']),
        showInitialLoadingState() {
            return this.fetchingUserTasks && !this.userTasks.length && !this.tasksLoadError
        },
        showErrorState() {
            return this.tasksLoadError && !this.fetchingUserTasks
        },
        itemsPerPageOptions() {
            return [10, 20, 50, this.maxTaskPerPage]
        },
        selectedTasks() {
            const checkedTasks = filter(this.taskCheckBoxData, { value: true })
            return checkedTasks.length ? checkedTasks.map(task => this.taskStatusTypes[task.key].value).join('') : -1
        }
    },
    watch: {
        options: {
            handler() {
                this.refetch()
            },
            deep: true
        },
        search() {
            this.searchInTasks()
        }
    },
    methods: {
        buildParams() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.options
            const params = {
                page,
                per_page: itemsPerPage,
                status: this.selectedTasks,
                operation: this.search
            }
            if (sortBy && sortBy.length > 0) {
                params.sort_by = sortBy
                params.sort_desc = sortDesc
            }
            return params
        },
        refetch() {
            // Wait until the v-data-table has initialised its options (itemsPerPage etc.).
            // Until then a fetch would go out with per_page=undefined; the table's initial
            // sync triggers the options watcher, which calls refetch() again with valid values.
            if (this.options.itemsPerPage == null) {
                return
            }
            const params = this.buildParams()
            // The v-data-table owns `options` via .sync and re-emits it on every internal
            // change (including when server-items-length hits 0 and it flips to client-side
            // pagination). Without this guard the options watcher would refetch on those
            // echo-backs, and each fetch would trigger another echo, causing an infinite loop.
            if (isEqual(params, this.params)) {
                return
            }
            this.params = params
            this.fetchTasks()
        },
        async fetchTasks() {
            try {
                const result = await this.$store.dispatch('userStore/fetchUserTasks', this.params)
                this.tasksLoadError = this.$isError(result)
            } catch (error) {
                this.tasksLoadError = true
            }
        },
        changeSelectedTasks() {
            this.options.page = 1
            this.refetch()
        },
        taskTypeLabel(status) {
            return find(this.taskCheckBoxData, { key: this.taskStatusTypes.get(status).key }).label
        },
        calculateDuration: function (date2, date1) {
            return calculateDuration(date2, date1)
        },
        truncateText: function (text, length) {
            return truncateText(text, length)
        },
        getTaskChipColor: function (status) {
            return this.colors[status] || 'info'
        },
        getTaskChipIcon: function (status) {
            return this.icons[status] || 'info'
        },
        getItemMessage(item) {
            const resultMessages = [
                'Task has been rejected.',
                'Task created.',
                'Task is waiting in queue.',
                'Task is in progress.',
                'Task is completed.',
                'Task failed.',
                'Task is archived.',
                'Task encountered errors.',
                'Task cancelled'
            ]
            return item.result || resultMessages[item.status]
        },
        taskIsCancellable(status) {
            return [this.taskStatusTypes.QUEUED.value, this.taskStatusTypes.RUNNING.value, this.taskStatusTypes.CREATED.value].includes(status)
        },
        async cancelThisTask({ tkid }) {
            try {
                await this.$axios.post(`/tasks/${tkid}/cancel`)
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Task successfully cancelled',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to cancel task.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.$store.dispatch('userStore/fetchUserTasks', this.params)
        }
    }
}
</script>

<style scoped lang="scss">
.v-input--selection-controls {
    margin-top: 0;
}
</style>
